/* Compounding Interest Calculator Logic — Interactive Math, URL Sync, Charts & CSV Export */

let compoundDonutChart = null;
let compoundGrowthChart = null;

function getCompoundingInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  const getSelect = (id, def = '') => {
    const el = document.getElementById(id);
    return el ? el.value : def;
  };

  return {
    principal: getVal('cmpPrincipal', 100000),
    deposit: getVal('cmpDeposit', 5000),
    rate: getVal('cmpRate', 10),
    years: getVal('cmpYears', 10),
    frequency: getSelect('cmpFrequency', 'monthly'),
    compoundFreq: getSelect('cmpCompoundFreq', 'monthly'),
  };
}

function getFrequencyPeriods(freq) {
  switch (freq) {
    case 'daily': return 365;
    case 'monthly': return 12;
    case 'quarterly': return 4;
    case 'semi-annually': return 2;
    case 'annually': return 1;
    default: return 12;
  }
}

function updateCompoundingUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('principal', params.principal);
    url.searchParams.set('deposit', params.deposit);
    url.searchParams.set('rate', params.rate);
    url.searchParams.set('years', params.years);
    url.searchParams.set('frequency', params.frequency);
    url.searchParams.set('compound', params.compoundFreq);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseCompoundingUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const principal = searchParams.get('principal');
  const deposit = searchParams.get('deposit');
  const rate = searchParams.get('rate');
  const years = searchParams.get('years');
  const frequency = searchParams.get('frequency');
  const compoundFreq = searchParams.get('compound');

  return {
    principal: principal !== null ? +principal : null,
    deposit: deposit !== null ? +deposit : null,
    rate: rate !== null ? +rate : null,
    years: years !== null ? +years : null,
    frequency: frequency !== null ? frequency : null,
    compoundFreq: compoundFreq !== null ? compoundFreq : null,
  };
}

function calculateCompounding(params) {
  const { principal, deposit, rate, years, frequency, compoundFreq } = params;

  const depositPeriodsPerYear = getFrequencyPeriods(frequency);
  const compoundPeriodsPerYear = getFrequencyPeriods(compoundFreq);

  const r = rate / 100;
  const totalYears = years;

  let currentBalance = principal;
  let totalInvested = principal;

  const schedule = [];
  const monthsTotal = totalYears * 12;
  const depositInterval = 12 / Math.min(12, depositPeriodsPerYear);

  for (let m = 1; m <= monthsTotal; m++) {
    if (m % depositInterval === 0 && deposit > 0) {
      currentBalance += deposit;
      totalInvested += deposit;
    }

    const monthlyRate = Math.pow(1 + r / compoundPeriodsPerYear, compoundPeriodsPerYear / 12) - 1;
    const monthInterest = currentBalance * monthlyRate;
    currentBalance += monthInterest;

    if (m % 12 === 0) {
      const year = m / 12;
      schedule.push({
        year,
        totalInvested: Math.round(totalInvested),
        totalInterest: Math.round(currentBalance - totalInvested),
        balance: Math.round(currentBalance),
      });
    }
  }

  const finalBalance = Math.round(currentBalance);
  const roundedInvested = Math.round(totalInvested);
  const roundedInterest = Math.max(0, finalBalance - roundedInvested);
  const multiplier = roundedInvested > 0 ? (finalBalance / roundedInvested).toFixed(2) : '1.00';

  return {
    finalBalance,
    totalInvested: roundedInvested,
    totalInterest: roundedInterest,
    multiplier,
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('cmpPrincipalDisplay', fmt(params.principal));
  setTxt('cmpDepositDisplay', fmt(params.deposit));
  setTxt('cmpRateDisplay', fmtPct(params.rate));
  setTxt('cmpYearsDisplay', `${params.years} yrs`);
}

function renderCompoundingResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('cmpVerdictHeadline', `Total Wealth: ${fmt(result.finalBalance)}`);
  setTxt('cmpVerdictDetail', `Your initial deposit of ${fmt(params.principal)} plus ${fmt(params.deposit)} ${params.frequency} contributions grew by ${result.multiplier}x with ${params.rate}% annual compounding over ${params.years} years.`);

  setTxt('cmpFinalBalanceValue', fmt(result.finalBalance));
  setTxt('cmpTotalInterestValue', `+${fmt(result.totalInterest)}`);
  setTxt('cmpTotalInvestedValue', fmt(result.totalInvested));
  setTxt('cmpMultiplierValue', `${result.multiplier}x`);

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.totalInvested, result.totalInterest);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('Compounding Chart rendering error:', err);
  }
}

function renderDonutChart(invested, interest) {
  const ctx = document.getElementById('cmpDonutChart');
  if (!ctx) return;

  if (compoundDonutChart) compoundDonutChart.destroy();

  compoundDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Total Principal Invested', 'Compounded Interest Earned'],
      datasets: [
        {
          data: [invested, interest],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent')],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('cmpGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const investedData = schedule.map((r) => r.totalInvested);
  const balanceData = schedule.map((r) => r.balance);

  if (compoundGrowthChart) compoundGrowthChart.destroy();

  compoundGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Total Accumulated Corpus',
          data: balanceData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderWidth: 2.5,
        },
        {
          label: 'Total Principal Invested',
          data: investedData,
          borderColor: cssVar('--chart-buy'),
          backgroundColor: 'rgba(52, 211, 153, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderWidth: 2.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick'), maxTicksLimit: 10 },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('cmpScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => `
    <tr>
      <td>Year ${row.year}</td>
      <td>${fmt(row.totalInvested)}</td>
      <td class="advantage-renter">+${fmt(row.totalInterest)}</td>
      <td class="advantage-buyer">${fmt(row.balance)}</td>
    </tr>
  `).join('');
}

function exportCompoundingCsv() {
  const params = getCompoundingInputs();
  const result = calculateCompounding(params);
  const headers = ['Year', 'Total Principal Invested', 'Compounded Interest Earned', 'Total Accumulated Corpus'];
  const rows = result.schedule.map((r) => [
    r.year, r.totalInvested, r.totalInterest, r.balance
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `compound-interest-schedule-${params.years}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applyCompoundingCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultPrincipal = 100000;
  let defaultDeposit = 5000;
  let maxPrincipal = 10000000;
  let maxDeposit = 500000;
  let stepPrincipal = 10000;
  let stepDeposit = 1000;

  if (curr === 'INR') {
    defaultPrincipal = 500000;
    defaultDeposit = 10000;
    maxPrincipal = 10000000;
    maxDeposit = 1000000;
    stepPrincipal = 50000;
    stepDeposit = 5000;
  } else if (curr === 'JPY') {
    defaultPrincipal = 1000000;
    defaultDeposit = 50000;
    maxPrincipal = 10000000;
    maxDeposit = 5000000;
    stepPrincipal = 100000;
    stepDeposit = 10000;
  }

  const pSlider = document.getElementById('cmpPrincipal');
  if (pSlider) {
    pSlider.max = maxPrincipal;
    pSlider.step = stepPrincipal;
    pSlider.value = overrideParams.principal ?? defaultPrincipal;
  }

  const dSlider = document.getElementById('cmpDeposit');
  if (dSlider) {
    dSlider.max = maxDeposit;
    dSlider.step = stepDeposit;
    dSlider.value = overrideParams.deposit ?? defaultDeposit;
  }
}

function recalculateCompounding() {
  const params = getCompoundingInputs();
  updateDisplays(params);
  updateCompoundingUrlParams(params);
  const result = calculateCompounding(params);
  renderCompoundingResults(result, params);
}

function initCompoundingCalculator() {
  if (!document.getElementById('cmpPrincipal')) return;

  const urlParams = parseCompoundingUrlParams();

  initCountry((country) => {
    applyCompoundingCountryRanges(country, urlParams);
    recalculateCompounding();
  });

  if (typeof currentCountry !== 'undefined') {
    applyCompoundingCountryRanges(currentCountry, urlParams);
  }

  if (urlParams.principal !== null && document.getElementById('cmpPrincipal')) {
    document.getElementById('cmpPrincipal').value = urlParams.principal;
  }
  if (urlParams.deposit !== null && document.getElementById('cmpDeposit')) {
    document.getElementById('cmpDeposit').value = urlParams.deposit;
  }
  if (urlParams.rate !== null && document.getElementById('cmpRate')) {
    document.getElementById('cmpRate').value = urlParams.rate;
  }
  if (urlParams.years !== null && document.getElementById('cmpYears')) {
    document.getElementById('cmpYears').value = urlParams.years;
  }
  if (urlParams.frequency !== null && document.getElementById('cmpFrequency')) {
    document.getElementById('cmpFrequency').value = urlParams.frequency;
  }
  if (urlParams.compoundFreq !== null && document.getElementById('cmpCompoundFreq')) {
    document.getElementById('cmpCompoundFreq').value = urlParams.compoundFreq;
  }

  const inputs = document.querySelectorAll('input[type="range"], select');
  inputs.forEach((el) => el.addEventListener('input', recalculateCompounding));

  const exportBtn = document.getElementById('exportCmpCsv');
  if (exportBtn) exportBtn.addEventListener('click', exportCompoundingCsv);

  recalculateCompounding();
}

document.addEventListener('DOMContentLoaded', initCompoundingCalculator);
